import { Autocomplete, Chip, TextField } from '@mui/material'

export interface MultiSelectFilterProps<T extends string> {
  label: string
  value: T[]
  options: readonly T[]
  /** Human-readable label for an option; the raw value is used when omitted. */
  getOptionLabel?: (option: T) => string
  onChange: (value: T[]) => void
}

/**
 * Multi-value filter control.
 *
 * Built on Autocomplete rather than a `Select multiple`: the previous control
 * was pinned to a fixed width and rendered its selection as plain chips, which
 * spilled outside the outlined box once two were picked and gave no way to drop
 * a value without reopening the menu and hunting for the ticked row (#60).
 *
 * Autocomplete gives each chip its own delete button, a clear-all affordance,
 * and a field that grows as the selection wraps.
 */
export const MultiSelectFilter = <T extends string>({ label, value, options, getOptionLabel, onChange }: MultiSelectFilterProps<T>) => (
  <Autocomplete
    multiple
    size="small"
    // Picking several values is the norm here, so keep the list open between clicks.
    disableCloseOnSelect
    options={options as T[]}
    value={value}
    getOptionLabel={option => getOptionLabel?.(option) ?? option}
    onChange={(_event, next) => {
      onChange([...next])
    }}
    renderTags={(selected, getTagProps) =>
      selected.map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index })
        // The per-option test id gives each chip — and its delete button — a
        // stable handle, since a chip carries no accessible role of its own.
        return (
          <Chip
            key={key}
            size="small"
            data-testid={`multi-select-chip-${option}`}
            label={getOptionLabel?.(option) ?? option}
            {...tagProps}
          />
        )
      })
    }
    renderInput={params => <TextField {...params} label={label} />}
    sx={{ minWidth: 220, maxWidth: 360 }}
  />
)
